import { FC } from 'react'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostShow, YoutubePostSettings } from 'api/clients/ScheduledPostsClient'
import { Globe, UserProfileAdd, Lock3 } from 'blaze-ui/icons'
import { IconProps } from 'blaze-ui/icons/Types'

export interface AIGenFieldConfig {
  key: string
  getInitialValue: (scheduledPost: ScheduledPostShow) => string | undefined
  getContextData: (
    scheduledPost: ScheduledPostShow,
    provider: ExternalIntegrationEnum,
  ) => Record<string, string | undefined>
  fieldToGenerate: string
  label: string
  isTextarea: boolean
}

export const AIGEN_FIELDS: AIGenFieldConfig[] = [
  {
    key: 'title',
    getInitialValue: (scheduledPost) => (scheduledPost?.post_settings as YoutubePostSettings)?.title,
    getContextData: (scheduledPost, provider) => ({
      source_title: (scheduledPost?.post_settings as YoutubePostSettings)?.title || 'Test Title',
      channel: provider,
      topic: 'Test Topic',
      filename: 'testfile.txt',
    }),
    fieldToGenerate: 'title',
    label: 'Title',
    isTextarea: false,
  },
  {
    key: 'description',
    getInitialValue: (scheduledPost) => (scheduledPost?.post_settings as YoutubePostSettings)?.description,
    getContextData: (scheduledPost, provider) => ({
      source_title: (scheduledPost?.post_settings as YoutubePostSettings)?.description || 'Test Description',
      channel: provider,
      topic: 'Test Description',
      filename: 'testfile.txt',
    }),
    fieldToGenerate: 'description',
    label: 'Description',
    isTextarea: true,
  },
]

export enum YoutubeVisibilityLevel {
  Public = 'public',
  Unlisted = 'unlisted',
  Private = 'private',
}

export const YOUTUBE_VISIBILITY_LEVELS = [
  YoutubeVisibilityLevel.Public,
  YoutubeVisibilityLevel.Unlisted,
  YoutubeVisibilityLevel.Private,
] as const

export const YOUTUBE_VISIBILITY_LABELS: Record<YoutubeVisibilityLevel, string> = {
  [YoutubeVisibilityLevel.Public]: 'Public',
  [YoutubeVisibilityLevel.Unlisted]: 'Unlisted',
  [YoutubeVisibilityLevel.Private]: 'Private',
}

export const YOUTUBE_VISIBILITY_ICONS: Record<YoutubeVisibilityLevel, FC<IconProps>> = {
  [YoutubeVisibilityLevel.Public]: Globe,
  [YoutubeVisibilityLevel.Unlisted]: UserProfileAdd,
  [YoutubeVisibilityLevel.Private]: Lock3,
}

export const YOUTUBE_DISABLED_KEYS: YoutubeVisibilityLevel[] = []

export type YoutubeCategoryItem = {
  kind: string
  etag: string
  id: string
  snippet: {
    title: string
    assignable: boolean
    channelId: string
  }
}

export interface YoutubeCategoryListResponse {
  kind: string
  etag: string
  items: YoutubeCategoryItem[]
}

export const YOUTUBE_AGE_LEVEL_OPTIONS = [
  { key: 'yes', title: 'Yes' },
  { key: 'no', title: 'No' },
]

export const YOUTUBE_CATEGORIES = {
  kind: 'youtube#videoCategoryListResponse',
  etag: 'QteLrrS_X7rM7rlcU_e7qa0embQ',
  items: [
    {
      kind: 'youtube#videoCategory',
      etag: 'grPOPYEUUZN3ltuDUGEWlrTR90U',
      id: '1',
      snippet: {
        title: 'Film & Animation',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
    {
      kind: 'youtube#videoCategory',
      etag: 'Q0xgUf8BFM8rW3W0R9wNq809xyA',
      id: '2',
      snippet: {
        title: 'Autos & Vehicles',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
    {
      kind: 'youtube#videoCategory',
      etag: 'qnpwjh5QlWM5hrnZCvHisquztC4',
      id: '10',
      snippet: {
        title: 'Music',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
    {
      kind: 'youtube#videoCategory',
      etag: 'HyFIixS5BZaoBdkQdLzPdoXWipg',
      id: '15',
      snippet: {
        title: 'Pets & Animals',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
    {
      kind: 'youtube#videoCategory',
      etag: 'PNU8SwXhjsF90fmkilVohofOi4I',
      id: '17',
      snippet: {
        title: 'Sports',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
    {
      kind: 'youtube#videoCategory',
      etag: 'ANnLQyzEA_9m3bMyJXMhKTCOiyg',
      id: '19',
      snippet: {
        title: 'Travel & Events',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
    {
      kind: 'youtube#videoCategory',
      etag: '0Hh6gbZ9zWjnV3sfdZjKB5LQr6E',
      id: '20',
      snippet: {
        title: 'Gaming',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
    {
      kind: 'youtube#videoCategory',
      etag: 'cHDaaqPDZsJT1FPr1-MwtyIhR28',
      id: '22',
      snippet: {
        title: 'People & Blogs',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
    {
      kind: 'youtube#videoCategory',
      etag: '3Uz364xBbKY50a2s0XQlv-gXJds',
      id: '23',
      snippet: {
        title: 'Comedy',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
    {
      kind: 'youtube#videoCategory',
      etag: '0srcLUqQzO7-NGLF7QnhdVzJQmY',
      id: '24',
      snippet: {
        title: 'Entertainment',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
    {
      kind: 'youtube#videoCategory',
      etag: 'bQlQMjmYX7DyFkX4w3kT0osJyIc',
      id: '25',
      snippet: {
        title: 'News & Politics',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
    {
      kind: 'youtube#videoCategory',
      etag: 'Y06N41HP_WlZmeREZvkGF0HW5pg',
      id: '26',
      snippet: {
        title: 'Howto & Style',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
    {
      kind: 'youtube#videoCategory',
      etag: 'yBaNkLx4sX9NcDmFgAmxQcV4Y30',
      id: '27',
      snippet: {
        title: 'Education',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
    {
      kind: 'youtube#videoCategory',
      etag: 'Mxy3A-SkmnR7MhJDZRS4DuAIbQA',
      id: '28',
      snippet: {
        title: 'Science & Technology',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
    {
      kind: 'youtube#videoCategory',
      etag: 'p3lEirEJApyEkuWpaGEHoF-m-aA',
      id: '29',
      snippet: {
        title: 'Nonprofits & Activism',
        assignable: true,
        channelId: 'UCBR8-60-B28hp2BmDPdntcQ',
      },
    },
  ],
}
